var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Button, Menu, MenuItem } from '@mui/material';
import React from 'react';
/**
 * A customizable button that displays a dropdown menu with clickable items.
 *
 * @param {MenuButtonProps} props - The props for the MenuButton component.
 * @param {React.ReactNode} props.children - The content inside the button.
 * @param {'small' | 'medium' | 'large'} [props.size] - The size of the button.
 * @param {'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'} [props.color] - The color theme of the button.
 * @param {boolean} [props.disabled] - Whether the button is disabled.
 * @param {number} [props.tabIndex] - Tab index for accessibility.
 * @param {React.ReactNode} [props.startIcon] - Icon to show at the start of the button.
 * @param {React.ReactNode} [props.endIcon] - Icon to show at the end of the button.
 * @param {'text' | 'outlined' | 'contained'} [props.variant] - Variant style of the button.
 * @param {boolean} [props.fullWidth] - Whether the button should take full width.
 * @param {'left' | 'right'} [props.anchor] - The horizontal alignment of the menu.
 * @param {boolean} [props.stopPropagationOnClick] - Whether to stop event propagation on click.
 * @param {{ label: string; href?: string; onClick?: () => void; disabled?: boolean; 'data-test'?: string }[]} props.menuItems - Array of menu items.
 * @param {SxProps<Theme>} [props.sx] - Custom styles for the button.
 * @param {string} [props.'data-test'] - Test identifier.
 * @param {string} [props.'aria-label'] - Aria-label for accessibility.
 * @returns {JSX.Element} A button that opens a dropdown menu when clicked.
 */
export const MenuButton = (props) => {
    var _a, _b;
    const [anchorEl, setAnchorEl] = React.useState();
    const handleClick = (event) => {
        if (props.stopPropagationOnClick) {
            event.stopPropagation();
        }
        setAnchorEl(event.currentTarget);
    };
    const handleClose = (event) => {
        if (props.stopPropagationOnClick) {
            event.stopPropagation();
        }
        setAnchorEl(undefined);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { size: props.size, color: props.color, disabled: props.disabled, tabIndex: props.tabIndex, startIcon: props.startIcon, endIcon: props.endIcon, variant: props.variant, fullWidth: props.fullWidth, sx: props.sx, onClick: handleClick, "data-test": props['data-test'], "aria-label": props['aria-label'], "aria-haspopup": 'menu', "aria-expanded": !!anchorEl }, props.children),
        React.createElement(Menu, { anchorEl: anchorEl, open: !!anchorEl, anchorOrigin: { horizontal: (_a = props.anchor) !== null && _a !== void 0 ? _a : 'left', vertical: 'bottom' }, transformOrigin: { horizontal: (_b = props.anchor) !== null && _b !== void 0 ? _b : 'left', vertical: 'top' }, onClose: handleClose }, props.menuItems.map((_a) => {
            var { onClick } = _a, menuItem = __rest(_a, ["onClick"]);
            return menuItem.href ? (React.createElement(MenuItem, { key: menuItem.label, component: 'a', href: menuItem.href, onClick: (event) => {
                    if (onClick) {
                        onClick();
                    }
                    handleClose(event);
                }, disabled: menuItem.disabled, "data-test": menuItem['data-test'] }, menuItem.label)) : (React.createElement(MenuItem, { key: menuItem.label, onClick: (event) => {
                    if (onClick) {
                        event.preventDefault();
                        onClick();
                    }
                    handleClose(event);
                }, disabled: menuItem.disabled, "data-test": menuItem['data-test'] }, menuItem.label));
        }))));
};
